import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Eye, Download } from "lucide-react";

const OB = { not_started: ["Not started", "bg-slate-100 text-slate-600"], in_progress: ["In progress", "bg-amber-100 text-amber-700"], submitted: ["Submitted", "bg-emerald-100 text-emerald-700"] };
const CL = {
  not_required: ["Not required", "bg-slate-100 text-slate-500"], draft: ["Draft", "bg-amber-100 text-amber-700"],
  awaiting_response: ["Awaiting response", "bg-sky-100 text-sky-700"], response_received: ["Response received", "bg-emerald-100 text-emerald-700"],
  part_received: ["Part received", "bg-amber-100 text-amber-700"], complete: ["Complete", "bg-emerald-100 text-emerald-700"],
  attention_required: ["Attention", "bg-red-100 text-red-700"],
};
const FILTERS = [
  ["all", "All"], ["awaiting_onboarding", "Awaiting onboarding"], ["in_progress", "In progress"],
  ["submitted", "Submitted"], ["changes", "Changes to review"], ["clearance_draft", "Clearance to review"],
  ["clearance_awaiting", "Clearance awaiting"],
];

export default function OnboardingTracker() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [issued, setIssued] = useState(null); // { client_name, letter_html }

  useEffect(() => { api.get("/onboarding-tracker").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const viewIssued = async (e, r) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/clearance/by-proposal/${r.proposal_id}`);
      setIssued({ client_name: r.client_name, letter_html: data?.snapshot?.letter_html || data?.letter_html || "" });
    } catch { toast.error("Could not load issued letter"); }
  };
  const downloadPdf = async (e, r) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/clearances/${r.clearance_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not fetch PDF"); }
  };

  const shown = useMemo(() => rows.filter((r) => {
    switch (filter) {
      case "awaiting_onboarding": return r.onboarding_status === "not_started";
      case "in_progress": return r.onboarding_status === "in_progress";
      case "submitted": return r.onboarding_status === "submitted";
      case "changes": return r.changes_to_review > 0;
      case "clearance_draft": return r.clearance_status === "draft";
      case "clearance_awaiting": return r.clearance_status === "awaiting_response";
      default: return true;
    }
  }), [rows, filter]);

  return (
    <div>
      <PageHeader title="Onboarding & Clearance" accent="teal" Icon={ClipboardCheck}
        subtitle="Every new engagement's onboarding and professional-clearance progress in one place." />
      <div className="p-4 md:p-8">
        <div className="flex flex-wrap gap-2 mb-5" data-testid="tracker-filters">
          {FILTERS.map(([k, l]) => {
            const n = k === "all" ? rows.length : rows.filter((r) =>
              (k === "awaiting_onboarding" && r.onboarding_status === "not_started") ||
              (k === "in_progress" && r.onboarding_status === "in_progress") ||
              (k === "submitted" && r.onboarding_status === "submitted") ||
              (k === "changes" && r.changes_to_review > 0) ||
              (k === "clearance_draft" && r.clearance_status === "draft") ||
              (k === "clearance_awaiting" && r.clearance_status === "awaiting_response")).length;
            return (
              <button key={k} onClick={() => setFilter(k)} data-testid={`tracker-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === k ? "bg-cass-navy text-white border-cass-navy" : "bg-white text-cass-slate border-border hover:border-cass-navy"}`}>
                {l} <span className="opacity-70">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="bg-white rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="tracker-table">
            <thead className="bg-cass-bone text-cass-slate text-xs uppercase tracking-wide">
              <tr><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Reference</th><th className="text-left px-4 py-3">LoE</th><th className="text-left px-4 py-3">Onboarding</th><th className="text-left px-4 py-3">Changes</th><th className="text-left px-4 py-3">Clearance</th><th className="text-left px-4 py-3">Issued letter</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shown.map((r) => {
                const ob = OB[r.onboarding_status] || OB.not_started;
                const cl = CL[r.clearance_status] || ["—", "bg-slate-50 text-slate-400"];
                return (
                  <tr key={r.proposal_id} className="hover:bg-cass-bone/40 cursor-pointer" onClick={() => navigate(`/proposals/${r.proposal_id}`)} data-testid={`tracker-row-${r.proposal_id}`}>
                    <td className="px-4 py-3 font-medium text-cass-navy">{r.client_name}</td>
                    <td className="px-4 py-3 text-cass-slate">{r.reference}</td>
                    <td className="px-4 py-3">{r.loe === "signed" ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Signed</Badge> : <Badge variant="outline">Pending</Badge>}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${ob[1]}`}>{ob[0]}</span></td>
                    <td className="px-4 py-3">{r.changes_to_review > 0 ? <Badge className="bg-amber-500">{r.changes_to_review} to review</Badge> : <span className="text-cass-slate/50">—</span>}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${cl[1]}`}>{cl[0]}</span></td>
                    <td className="px-4 py-3">
                      {r.clearance_sent ? (
                        <div className="flex gap-2">
                          <button onClick={(e) => viewIssued(e, r)} className="inline-flex items-center gap-1 text-xs text-cass-teal hover:underline" data-testid={`tracker-view-${r.proposal_id}`}><Eye className="w-3.5 h-3.5" />View</button>
                          <button onClick={(e) => downloadPdf(e, r)} className="inline-flex items-center gap-1 text-xs text-cass-navy hover:underline" data-testid={`tracker-pdf-${r.proposal_id}`}><Download className="w-3.5 h-3.5" />PDF</button>
                        </div>
                      ) : <span className="text-cass-slate/50">—</span>}
                    </td>
                  </tr>
                );
              })}
              {!shown.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-cass-slate" data-testid="tracker-empty">Nothing here yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="tracker-issued-dialog">
          <DialogHeader><DialogTitle>Issued clearance letter — {issued?.client_name}</DialogTitle><DialogDescription>Preserved historical record of the exact letter issued.</DialogDescription></DialogHeader>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: issued?.letter_html || "<em>No issued letter found.</em>" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
